import React from 'react';
import { List, ListSubheader } from '@material-ui/core';
import { MenuPopperAllowedSteps } from '../MenuPopper/MenuPopperAllowedSteps';
import { useTranslation } from 'react-i18next';
import { MenuPopperProjectOptions } from '../MenuPopper/MenuPopperProjectOptions';
import { MenuPopperCurrentActivity } from '../MenuPopper/MenuPopperCurrentActivity';
import { MenuPopperDeleteProject } from '../MenuPopper/MenuPopperDeleteProject';
import type { ProsjektSteg } from '@klp/formgenerator/interfaces';
import { REGISTRERING_STATUS } from '@klp/formgenerator/interfaces';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { HideActionsElementType } from './ProsjektActions';
import type {
    GodkjenningProsessResponse,
    GodkjenningProsessSimple,
} from '@klp/prosjekt/interfaces';
import {
    getActivityAnsvarlig,
    getRoleHolderBrukerId,
} from '@klp/prosjekt/utils';
import { useMapEmailToUser } from '@klp/prosjekt/hooks';

const styles = () => ({
    list: { width: '100%' },
});

type ProsjektActionsListProps = {
    prosjekt: GodkjenningProsessResponse | GodkjenningProsessSimple;
    isEDAdmin: boolean | undefined;
    allowedSteps: Array<ProsjektSteg> | undefined;
    selectedElement?: HideActionsElementType;
    navigateOnDelete?: boolean;
    onClose?: () => void;
};

export const ProsjektActionsList: React.FC<ProsjektActionsListProps> = ({
    prosjekt,
    prosjekt: {
        prosjektId,
        prosessInstansId,
        navn,
        stegNavn,
        registreringStatus,
        prosessDefinisjonKey,
        prosjektskjemaType,
    },
    isEDAdmin = false,
    allowedSteps,
    selectedElement,
    navigateOnDelete = false,
    onClose,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { handleMapping: mapEmailToUser } = useMapEmailToUser();

    const handleListClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const prosjektIsFullResponse = (
        prosjektObject: GodkjenningProsessResponse | GodkjenningProsessSimple,
    ): prosjektObject is GodkjenningProsessResponse => {
        return 'prosjekt' in prosjektObject;
    };

    const processIsOnGoing = registreringStatus === REGISTRERING_STATUS.NY;
    const canDeleteRow = processIsOnGoing || isEDAdmin;

    // The role holder (e.g. the Prosjektleder on the process) and the person the current task
    // is assigned to may differ. Reminders go to the assignee, while reassigning changes the role.
    const isFullResponse = prosjektIsFullResponse(prosjekt);
    const eierRolle = isFullResponse
        ? (prosjekt.currentAktivitet?.kandidatGruppeEier ?? undefined)
        : (prosjekt.kandidatGruppeEier ?? undefined);
    const roleHolder = isFullResponse
        ? getActivityAnsvarlig(prosjekt, eierRolle ?? '')
        : mapEmailToUser(getRoleHolderBrukerId(prosjekt, eierRolle)) ||
          undefined;
    const taskAssignee =
        mapEmailToUser(
            isFullResponse
                ? prosjekt.currentAktivitet?.tildeltTil
                : prosjekt.ansvarlig,
        ) || undefined;

    return (
        <List
            data-testid="ProsjektActions"
            className={classes.list}
            onClick={handleListClick}
        >
            <MenuPopperAllowedSteps
                prosjektId={prosjektId}
                tillattNesteSteg={allowedSteps}
                prosjektskjemaTypeId={prosjektskjemaType?.id}
                rekkefolgenummer={prosjektskjemaType?.rekkefolgenummer}
            />

            <ListSubheader disableSticky={true}>
                {t('prosjekt.table.actionMenuItems.currentHeader', {
                    stegNavn: stegNavn,
                })}
            </ListSubheader>

            <MenuPopperProjectOptions
                prosjektId={prosjektId}
                prosessInstansId={prosessInstansId}
                prosessNavn={navn}
                prosessDefinisjonKey={prosessDefinisjonKey}
                selectedElement={selectedElement}
            />
            <MenuPopperCurrentActivity
                isCurrentActivitySet={processIsOnGoing}
                prosessInstansId={prosessInstansId}
                role={eierRolle}
                roleHolder={roleHolder}
                taskAssignee={taskAssignee}
            />

            <MenuPopperDeleteProject
                canDelete={canDeleteRow}
                prosessInstansId={prosessInstansId}
                navigateOnDelete={navigateOnDelete}
                onClose={onClose}
            />
        </List>
    );
};
